import React, { useEffect, useState, useRef } from "react";
import { CheckIcon, ChevronLeftIcon, ChevronRightIcon, ArrowRightIcon } from "lucide-react";
import { AttendanceTags } from "../../../types";

interface TagSelectorProps {
  field: AttendanceTags | null;
  isOpen: boolean;
  selected: { value_code: string; value_text: string }[];
  attendanceTagsOptions: AttendanceTags[];
  onClose: () => void;
  onSelect: (
    rootFieldCode: string,
    selected: { value_code: string; value_text: string }[],
    allSelections?: { 
      field_code: string;
      field_values: { value_code: string; value_text: string }[];
    }[]
  ) => void;
  fieldStack: (AttendanceTags | null)[];
  setFieldStack: React.Dispatch<React.SetStateAction<(AttendanceTags | null)[]>>;
  localSelectedMap: Record<string, { value_code: string; value_text: string }[]>;
  setLocalSelectedMap: React.Dispatch<
    React.SetStateAction<Record<string, { value_code: string; value_text: string }[]>>
  >;
}

const AttendanceTagSelector: React.FC<TagSelectorProps> = ({
  field,
  isOpen,
  selected,
  attendanceTagsOptions,
  onClose,
  onSelect,
  fieldStack,
  setFieldStack,
  localSelectedMap,
  setLocalSelectedMap,
}) => {
  // Reference for the options container to enable scrolling
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  
  // Store the raw data map alongside grouped data
  const [rawDataMap, setRawDataMap] = useState<Record<string, AttendanceTags[]>>({});
  
  // Track current step (can go beyond 2 now)
  const [currentStep, setCurrentStep] = useState(1);
  
  // State to store selected items at each level
  const [selectionsByLevel, setSelectionsByLevel] = useState<{
    [level: number]: { value_code: string; value_text: string }[]
  }>({});
  
  // State to store the current field being viewed at each level
  const [currentFieldByLevel, setCurrentFieldByLevel] = useState<{
    [level: number]: AttendanceTags | null
  }>({});
  
  // Maximum steps available for the current selection path
  const [maxSteps, setMaxSteps] = useState(1);
  
  // Effect to prevent body scrolling when the bottom sheet is open
  useEffect(() => {
    if (isOpen) {
      // Save the original overflow style
      const originalStyle = window.getComputedStyle(document.body).overflow;
      
      // Prevent scrolling on the body
      document.body.style.overflow = 'hidden';
      
      // Re-enable scrolling when component unmounts or sheet closes
      return () => {
        document.body.style.overflow = originalStyle;
      };
    }
  }, [isOpen]);
  
  // Calculate all possible child fields for the current selection path
  useEffect(() => {
    if (isOpen && field) {
      // Build the raw data map by parent_labor_dimension_id
      const newRawDataMap: Record<string, AttendanceTags[]> = {};
      
      // First, group all tags by their parent ID
      attendanceTagsOptions.forEach(tag => {
        const parentId = tag.parent_labor_dimension_id || 'root';
        if (!newRawDataMap[parentId]) {
          newRawDataMap[parentId] = [];
        }
        newRawDataMap[parentId].push(tag);
      });
      
      setRawDataMap(newRawDataMap);
      
      // Determine max steps by finding the longest possible path
      let maxLevel = 1;
      const findMaxDepth = (fieldId: string, currentDepth: number) => {
        maxLevel = Math.max(maxLevel, currentDepth);
        
        // Check if this field has children
        const children = newRawDataMap[fieldId];
        if (children && children.length > 0) {
          children.forEach(child => {
            findMaxDepth(String(child.labor_dimensions_id), currentDepth + 1);
          });
        }
      };
      
      // Start with root fields
      const rootField = fieldStack[0];
      if (rootField) {
        findMaxDepth(String(rootField.labor_dimensions_id), 1);
      }
      
      setMaxSteps(maxLevel);
      
      // Initialize the component state based on the field stack
      if (fieldStack.length > 0) {
        // Set current step based on fieldStack length
        setCurrentStep(fieldStack.length);
        
        // Initialize current fields and selections based on fieldStack
        const newCurrentFieldByLevel: {[level: number]: AttendanceTags | null} = {};
        const newSelectionsByLevel: {[level: number]: {value_code: string; value_text: string}[]} = {};
        
        fieldStack.forEach((stackField, index) => {
          if (stackField) {
            const level = index + 1;
            newCurrentFieldByLevel[level] = stackField;
            newSelectionsByLevel[level] = localSelectedMap[stackField.field_code] || [];
          }
        });
        
        setCurrentFieldByLevel(newCurrentFieldByLevel);
        setSelectionsByLevel(newSelectionsByLevel);
      } else {
        // Initialize with just the root field
        setCurrentStep(1);
        setCurrentFieldByLevel({ 1: field });
        setSelectionsByLevel({ 1: localSelectedMap[field.field_code] || [] });
      }
    }
  }, [isOpen, field, fieldStack, attendanceTagsOptions, localSelectedMap]);

  if (!isOpen || !field) {
    // console.log('AttendanceTagSelector: isOpen=', isOpen, 'field=', field);
    return null;
  }

  // Parse options from current field's field_json only
  const getOptions = (f: AttendanceTags): { value_code: string; value_text: string }[] => {
    // We will only use field_json which would have both code and text
    /* Commenting out field_values lookup as requested
    if (f.field_values && Array.isArray(f.field_values) && f.field_values.length > 0) {
      // Filter out empty strings and map to the expected format
      const validValues = f.field_values.filter(value => value && value.trim() !== "");
      if (validValues.length > 0) {
        return validValues.map((value: string) => ({
          value_code: value, // Use the value itself as the code
          value_text: value, // Use the value as the display text
        }));
      }
    }
    */
    
    // Use field_json exclusively
    if (!f.field_json) {
      console.log('No field_json found for field:', f.field_name);
      return [];
    }
    
    if (Array.isArray(f.field_json)) {
      console.log('field_json is array, length:', f.field_json.length);
      return f.field_json.map((item: any) => ({
        value_code: item.field_value_code || item.value_code || item.code || '',
        value_text: item.field_value_text || item.value_text || item.text || item.name || '',
      }));
    }
    
    if (typeof f.field_json === "string") {
      try {
        console.log('Parsing field_json string');
        const arr = JSON.parse(f.field_json);
        if (Array.isArray(arr)) {
          return arr.map((item: any) => ({
            value_code: item.field_value_code || item.value_code || item.code || '',
            value_text: item.field_value_text || item.value_text || item.text || item.name || '',
          }));
        }
        console.log('Parsed field_json is not an array');
        return [];
      } catch (error) {
        console.error('Error parsing field_json:', error);
        return [];
      }
    }
    
    console.log('field_json is not array or string:', typeof f.field_json);
    return [];
  };

  // Get current field for the active step
  const currentField = currentFieldByLevel[currentStep] || field;
  const isMulti = currentField.multiselect === "true";
  const localSelected = localSelectedMap[currentField.field_code] || [];
  
  // Add debug logging for troubleshooting
  console.log('Current field:', currentField.field_name, 'field_json:', currentField.field_json);
  console.log('Options:', getOptions(currentField));
  
  // Get child fields from raw data map
  const childFields = rawDataMap[String(currentField.labor_dimensions_id)] || [];
  
  // Check if there are more levels after the current one
  const hasNextLevel = childFields.length > 0 || currentStep < maxSteps;
  
  // Helper to check if an option is selected
  const isSelected = (optionCode: string) => {
    console.log('Checking if selected:', optionCode, localSelected);
    return localSelected.some((sel) => sel.value_code === optionCode);
  };
  
  // Toggle selection for the current level
  const toggleOption = (option: { value_code: string; value_text: string }) => {
    // Get the field for the current step
    const currentField = currentFieldByLevel[currentStep] || field;
    const isMulti = currentField.multiselect === "true";
    
    let newSelected: { value_code: string; value_text: string }[];
    
    if (isMulti) {
      const exists = localSelected.some((sel) => sel.value_code === option.value_code);
      newSelected = exists
        ? localSelected.filter((sel) => sel.value_code !== option.value_code)
        : [...localSelected, option];
    } else {
      newSelected = [option];
    }
    
    // Update local selected map
    setLocalSelectedMap((prev) => ({
      ...prev,
      [currentField.field_code]: newSelected
    }));
    
    // Update selections by level
    setSelectionsByLevel(prev => ({
      ...prev,
      [currentStep]: newSelected
    }));
    
    // If deselecting at any level, clear child selections for that option
    if (isMulti) {
      const exists = localSelected.some((sel) => sel.value_code === option.value_code);
      if (exists && childFields.length > 0) {
        // Find all descendants of this field that might need clearing
        const findAllDescendants = (fieldId: string): string[] => {
          const directChildren = attendanceTagsOptions.filter(
            (tag) => tag.parent_labor_dimension_id === fieldId
          );
          
          let allDescendants: string[] = directChildren.map(c => c.field_code);
          
          for (const child of directChildren) {
            allDescendants = [
              ...allDescendants,
              ...findAllDescendants(String(child.labor_dimensions_id))
            ];
          }
          
          return allDescendants;
        };
        
        const allDescendants = findAllDescendants(String(currentField.labor_dimensions_id));
        
        if (allDescendants.length > 0) {
          setLocalSelectedMap((prev) => {
            const updated = { ...prev };
            allDescendants.forEach(code => {
              if (updated[code]) {
                updated[code] = updated[code].filter(
                  item => !item.value_code.includes(option.value_code)
                );
              }
            });
            return updated;
          });
        }
      }
    }
  };

  // Go to the next level in the hierarchy
  const goToNextLevel = () => {
    const currentSelections = localSelectedMap[currentField.field_code] || [];
    
    // Only proceed if we have selections at the current level
    if (currentSelections.length > 0) {
      // Lock UI to prevent multiple clicks
      const buttons = document.querySelectorAll('button');
      buttons.forEach(btn => { btn.disabled = true });
      
      const nextStep = currentStep + 1;
      
      // Find the next field in the hierarchy
      let nextField: AttendanceTags | null = null;
      
      if (childFields.length > 0) {
        nextField = childFields[0]; // Default to first child
      }
      
      // Update the field stack to include the next field
      const newFieldStack = [...fieldStack];
      if (nextField && !newFieldStack.includes(nextField)) {
        newFieldStack.push(nextField);
      }
      
      setFieldStack(newFieldStack);
      
      // Initialize the next level's selections if needed
      if (nextField) {
        const fieldCode = nextField.field_code;
        setLocalSelectedMap(prev => ({
          ...prev,
          [fieldCode]: prev[fieldCode] || []
        }));
      }
      
      // Update current fields by level
      setCurrentFieldByLevel(prev => ({
        ...prev,
        [nextStep]: nextField
      }));
      
      // Move to next step
      setTimeout(() => {
        setCurrentStep(nextStep);
        
        // If we have an options container ref, scroll to top
        if (optionsContainerRef.current) {
          optionsContainerRef.current.scrollTop = 0;
        }
        
        // Re-enable buttons after state change
        setTimeout(() => {
          buttons.forEach(btn => { btn.disabled = false });
        }, 50);
      }, 50);
    }
  };

  // Change to a different field at the current level
  const switchField = (newField: AttendanceTags) => {
    // Only perform the switch if it's different from the current field
    if (newField.labor_dimensions_id !== currentField.labor_dimensions_id) {
      // Update current field for this level
      setCurrentFieldByLevel(prev => ({
        ...prev,
        [currentStep]: newField
      }));
      
      // Initialize this field's selection if needed
      setLocalSelectedMap(prev => ({
        ...prev,
        [newField.field_code]: prev[newField.field_code] || []
      }));
      
      // Update the field stack
      const newFieldStack = [...fieldStack];
      newFieldStack[currentStep - 1] = newField;
      setFieldStack(newFieldStack);
      
      // If we have an options container ref, scroll to top
      if (optionsContainerRef.current) {
        optionsContainerRef.current.scrollTop = 0;
      }
    }
  };

  // Navigate back to previous level or close
  const goBack = () => {
    // Prevent any state flicker by disabling button temporarily
    const button = document.activeElement as HTMLButtonElement;
    if (button) button.disabled = true;
    
    if (currentStep > 1) {
      // Go back to the previous step
      const previousStep = currentStep - 1;
      
      // Update field stack by removing the current field
      const newFieldStack = fieldStack.slice(0, previousStep);
      setFieldStack(newFieldStack);
      
      setCurrentStep(previousStep);
      
      // If we have an options container ref, scroll to top
      if (optionsContainerRef.current) {
        optionsContainerRef.current.scrollTop = 0;
      }
      
      // Re-enable button after state change
      setTimeout(() => {
        if (button) button.disabled = false;
      }, 50);
    }
    // Note: If currentStep === 1, this function shouldn't be called 
    // since the header button now directly calls onClose()
  };

  // Complete selection and close the selector
  const done = () => {
    if (fieldStack.length === 0) return;
    const rootField = fieldStack[0];
    if (!rootField) return;
    
    // Prevent any state flicker by disabling button temporarily
    const button = document.activeElement as HTMLButtonElement;
    if (button) button.disabled = true;
    
    // Prepare the final output in the exact format needed for the API
    const allSelections: { 
      field_code: string;
      field_values: { value_code: string; value_text: string }[];
    }[] = [];
    
    // Process all fields with selections - using field_values instead of field_json
    // to match the API expectations
    Object.entries(localSelectedMap).forEach(([fieldCode, selections]) => {
      if (selections.length > 0) {
        // Add this field as a separate item in the format required by the API
        allSelections.push({
          field_code: fieldCode,
          field_values: selections
        });
      }
    });
    
    // Get the root selections for backward compatibility
    const rootSelections = localSelectedMap[rootField.field_code] || [];
    
    // Only call onSelect if we have selections
    if (allSelections.length > 0) {
      // Pass all selections to the parent component in the exact format needed for the API
      onSelect(rootField.field_code, rootSelections, allSelections);
    }
    
    // Close the selector
    onClose();
    
    // Reset state
    setTimeout(() => {
      setFieldStack([]);
      setCurrentStep(1);
      setCurrentFieldByLevel({});
      setSelectionsByLevel({});
    }, 50);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-[9999] flex flex-col touch-none mx-auto md:w-1/2">
      <div className="w-full h-full bg-white flex flex-col">
        {/* Mobile-friendly header */}
        <div className="flex justify-between items-center p-4 border-b">
          <button
            onClick={() => {
              console.log('Close button clicked, currentStep=', currentStep);
              if (currentStep === 1) {
                console.log('Calling onClose');
                onClose();
              } else {
                console.log('Calling goBack');
                goBack();
              }
            }}
            className="flex items-center text-blue-600 font-semibold"
            aria-label={currentStep === 1 ? "Close" : "Back"}
          >
            <ChevronLeftIcon size={20} />
            <span className="ml-1 text-sm">{currentStep === 1 ? "Close" : "Back"}</span>
          </button>
          
          {/* Spacer to center the right button */}
          <div className="flex-1"></div>
          
          {/* Next button moved to top right instead of bottom */}
          {hasNextLevel && currentStep < maxSteps && (
            <button
              onClick={goToNextLevel}
              disabled={localSelected.length === 0}
              className={`flex items-center ${
                localSelected.length > 0 
                  ? 'text-blue-600' 
                  : 'text-gray-400 cursor-not-allowed'
              }`}
            >
              <span className="mr-1 text-sm font-medium">Next</span>
              <ArrowRightIcon size={18} />
            </button>
          )}
          
          {/* Show Done button in the top right for the final step */}
          {currentStep === maxSteps && (
            <button
              onClick={done}
              className="flex items-center text-green-600 font-semibold"
            >
              <span className="mr-1 text-sm">Save</span>
              <CheckIcon size={18} />
            </button>
          )}
        </div>

        {/* Step indicator - scrollable horizontally */}
        {/* <div className="flex items-center overflow-x-auto py-3 px-4 border-b bg-white">
          {/* Create a step indicator for each possible step up to maxSteps */}
          {/* {Array.from({ length: maxSteps }, (_, i) => {
            const stepNumber = i + 1;
            const stepField = currentFieldByLevel[stepNumber];
            
            return (
              <React.Fragment key={stepNumber}>
                {/* Step circle */}
                {/* <div className="flex flex-col items-center">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center ${
                    currentStep === stepNumber ? 'bg-blue-600 text-white' : 'bg-gray-200'
                  }`}>
                    <span className="text-xs font-medium">{stepNumber}</span>
                  </div>
                  <span className={`text-xs mt-1 max-w-[70px] text-center truncate ${
                    currentStep === stepNumber ? 'text-blue-600 font-medium' : 'text-gray-500'
                  }`}>
                    {stepField?.field_name || `Step ${stepNumber}`}
                  </span>
                </div>
                
                {/* Connecting line (not after the last item) */}
                {/* {stepNumber < maxSteps && (
                  <div className="h-0.5 w-8 bg-gray-200 mx-1"></div>
                )} */}
              {/* </React.Fragment>
            );
          })} */}
        {/* </div> */}

        {/* Content area with title moved inside */}
        <div 
          ref={optionsContainerRef}
          className="flex-1 overflow-y-auto p-4"
        >
          {/* Title for the content section */}
          <h3 className="text-lg font-medium mb-3">
            Select {isMulti ? "one or more" : "a"} {currentField.field_name}
          </h3>
          
          {/* Show selections from previous levels if we're past step 1 */}
          {currentStep > 1 && (
            <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700 mb-2 font-medium">Previous selections:</p>
              <div className="flex flex-wrap gap-2">
                {Array.from({ length: currentStep - 1 }, (_, i) => {
                  const prevStep = i + 1;
                  const prevField = currentFieldByLevel[prevStep];
                  const prevSelections = selectionsByLevel[prevStep] || [];
                  
                  if (!prevField || prevSelections.length === 0) return null;
                  
                  return (
                    <div key={`prev-step-${prevStep}`} className="flex items-center">
                      <span className="text-xs text-gray-500 mr-1">{prevField.field_name}:</span>
                      <div className="bg-blue-100 text-blue-800 px-2 py-1 rounded-md text-xs font-medium">
                        {prevSelections.map((s, idx) => (
                          <span key={`${s.value_code}-${idx}`}>{s.value_text}{idx < prevSelections.length - 1 ? ', ' : ''}</span>
                        ))}
                      </div>
                      {i < currentStep - 2 && <span className="mx-1 text-gray-400">→</span>}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          
          {/* Child field tabs if there are multiple at the current level */}
          {childFields.length > 1 && (
            <div className="flex overflow-x-auto mb-4 pb-2 sticky top-0 bg-white">
              {childFields.map((child) => {
                const hasSelections = (localSelectedMap[child.field_code] || []).length > 0;
                return (
                  <button
                    key={child.labor_dimensions_id}
                    className={`px-4 py-2 mr-2 whitespace-nowrap rounded-full ${
                      currentField?.labor_dimensions_id === child.labor_dimensions_id
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-100 text-gray-700'
                    }`}
                    onClick={() => switchField(child)}
                  >
                    <span className="truncate max-w-[120px] inline-block">{child.field_name}</span>
                    {hasSelections && (
                      <span className="ml-1 inline-flex items-center justify-center w-4 h-4 text-xs font-medium bg-white text-blue-800 rounded-full">
                        {(localSelectedMap[child.field_code] || []).length}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          )}
          
          {/* Options list - scrollable */}
          <div className="space-y-2">
            {getOptions(currentField).length === 0 && (
              <p className="text-center text-gray-500 py-4">No options available.</p>
            )}
            
            {getOptions(currentField).map((option) => {
              const selected = isSelected(option.value_code);
              return (
                <div
                  key={option.value_code}
                  className={`flex items-center justify-between p-3 border ${
                    selected ? 'border-blue-500 bg-blue-50' : 'border-gray-200'
                  } rounded-lg cursor-pointer active:bg-gray-100 transition-colors`}
                  onClick={() => toggleOption(option)}
                >
                  <span className="select-none font-medium">{option.value_text}</span>
                  {selected && (
                    <div className="bg-blue-100 rounded-full p-1">
                      <CheckIcon size={16} color="blue" />
                    </div>
                  )}
                </div>
              );
            })}
          </div>
          
          {/* Show comma-separated selections at the last stage */}
          {/* {currentStep === maxSteps && (
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-100">
              <p className="text-sm text-blue-700 mb-2 font-medium">
                Your selections across all levels:
              </p>
              <div className="bg-white p-3 rounded-md border border-blue-200 space-y-3">
                {/* Show all selections from each level */}
                {/* {Object.entries(localSelectedMap).map(([fieldCode, selections]) => {
                  if (selections.length === 0) return null;
                  
                  // Find the field this belongs to
                  const fieldDetails = attendanceTagsOptions.find(tag => tag.field_code === fieldCode);
                  if (!fieldDetails) return null;
                  
                  return (
                    <div key={fieldCode} className="border-b border-gray-100 pb-2 last:border-b-0 last:pb-0">
                      <p className="text-sm text-gray-600 font-medium">
                        {fieldDetails.field_name}:
                      </p>
                      <p className="text-gray-800 font-medium">
                        {selections.map(s => s.value_text).join(', ')}
                      </p>
                    </div>
                  );
                })} */}
              {/* </div>
            </div>
          )} */}
          
          {/* Special case: if we're at the final step/level and no more children */}
          {currentStep === maxSteps && getOptions(currentField).length === 0 && (
            <div className="mt-4 p-4 bg-green-50 rounded-lg border border-green-100 text-center">
              <p className="text-green-700 font-medium">Your selection is complete!</p>
              <p className="text-sm text-green-600 mt-1">Tap "Done" above to confirm.</p>
            </div>
          )}
          
          {/* Bottom padding to ensure content is not hidden behind any buttons */}
          <div className="h-16"></div>
        </div>
      </div>
    </div>
  );
};

export default AttendanceTagSelector;
